import { useEffect, useLayoutEffect, useRef } from 'react';
import { Editor, Transforms, Element, ReactEditor } from 'domains/reporter/RichTextEditor/core';
import { PARAGRAPH_PLUGIN_ID } from 'domains/reporter/RichTextEditor/plugins';
import { selectNextHeadingSectionBracket } from 'domains/reporter/RichTextEditor/utils/bracketNavigation';
import { getLowestBracket, partialEditor } from 'domains/reporter/RichTextEditor/utils';
import { logger } from 'modules/logger';

/**
 *
 * Gets the first bracket in the editor (respecting merge field navigation preference).
 * If no bracket is found, it will fall back to heading-section navigation and then paragraph fallback.
 */
export const performDefaultSelection = (
  editor: Editor,
  {
    ignoreMergeFields,
  }: {
    ignoreMergeFields: boolean;
  } = { ignoreMergeFields: false }
) => {
  const lowestBracket = getLowestBracket(editor, {
    ignoreMergeFields,
  });

  if (lowestBracket != null) {
    Transforms.select(editor, Editor.range(editor, lowestBracket[1]));
    ReactEditor.focus(editor);
    return;
  }

  const node = selectNextHeadingSectionBracket(editor, {
    startPoint: Editor.end(editor, []),
    fallbackPoint: 'end',
    ignoreMergeFieldsInNavigation: ignoreMergeFields,
  });

  // Focus the editor after selection if a node was found
  if (node != null) {
    ReactEditor.focus(editor);
  }

  if (node == null) {
    // In this case there is no headings or brackets in the editor so we
    // set the selection to be the first paragraph.
    try {
      const start = Editor.start(editor, []);
      const startBlockNodeEntry = Editor.node(editor, start, {
        depth: 1,
      });
      if (
        startBlockNodeEntry != null &&
        Element.isElement(startBlockNodeEntry[0]) &&
        startBlockNodeEntry[0].type === PARAGRAPH_PLUGIN_ID
      ) {
        Transforms.select(editor, startBlockNodeEntry[1]);
        ReactEditor.focus(editor);
      } else {
        const firstParagraphNodeEntry = Editor.next(editor, {
          at: start,
          match: (n) => Element.isElement(n) && n.type === PARAGRAPH_PLUGIN_ID,
        });
        if (firstParagraphNodeEntry != null) {
          Transforms.select(editor, firstParagraphNodeEntry[1]);
          ReactEditor.focus(editor);
        } else {
          logger.error('[RichTextEditor] Cannot find default selection for editor', {
            editor: partialEditor(editor),
            ignoreMergeFields,
          });
        }
      }
    } catch (err: any) {
      const errorMessage = '[RichTextEditor] Error occurred during defaultSelection';

      logger.error(
        errorMessage,
        {
          editor: partialEditor(editor),
          ignoreMergeFields,
        },
        err
      );
    }
  }
};

export const useRichTextEditorDefaultSelection = (
  editor: Editor | null,
  {
    enableDefaultSelection = true,
    ignoreMergeFields = false,
    autoloadTemplateState,
  }: {
    enableDefaultSelection?: boolean;
    ignoreMergeFields?: boolean;
    autoloadTemplateState?: 'pending' | 'success' | 'skipped';
  } = {}
) => {
  const hasDefaultSelectionRef = useRef(false);

  // Isolate selection manipulation in useLayoutEffect to run in render cycle
  useLayoutEffect(() => {
    if (!editor) return;

    // If a template is autoloading, allow that process to set the selection instead.
    if (
      !hasDefaultSelectionRef.current &&
      enableDefaultSelection &&
      autoloadTemplateState !== 'pending'
    ) {
      performDefaultSelection(editor, {
        ignoreMergeFields,
      });
      hasDefaultSelectionRef.current = true;
    }
  }, [enableDefaultSelection, editor, autoloadTemplateState, ignoreMergeFields]);

  useEffect(() => {
    hasDefaultSelectionRef.current = false;
  }, [editor]);

  return {
    performDefaultSelection: (customIgnoreMergeFields?: boolean) =>
      performDefaultSelection(editor, {
        ignoreMergeFields: customIgnoreMergeFields ?? ignoreMergeFields,
      }),
  };
};
